/*
 * Claim Template area (audit Phase 3.3). Replaces wwwroot/js/ClaimTemplate/List.js
 * and wwwroot/js/ClaimTemplate/Detail.js with one module on the Phase 3.1 core.
 * No jQuery.
 *
 * A Claim Template is a named set of claims stamped onto a new Application,
 * Resource or Person, plus the Theme & Branding palette a domain hands its sign-in
 * pages. The detail modal renders both view-mode and edit-mode markup at once and
 * a `template-edit-mode` class on .claimtemplate-modal decides which is visible,
 * so entering and leaving edit mode costs no round trip.
 *
 * Edits accumulate in the DOM until Save. Each row carries a data-state that
 * decides what SaveAll fires for it:
 *   new       -> POST ClaimTemplate/ItemEdit with Id=0
 *   removed   -> DELETE ClaimTemplate/ItemDelete/{id}
 *   unchanged -> POST ClaimTemplate/ItemEdit (re-saved naively; the server update
 *                is idempotent, so the cost is one redundant write per row --
 *                well under the cost of tracking dirtiness in the client)
 *
 * The operations used to be jQuery Deferreds collected by $.when; they are plain
 * promises now, each resolving to {ok} so one failure cannot reject the batch and
 * hide the rows that did save.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, closest, one } from '../core/dom';
import { serialize } from '../core/forms';
import { modelStateSummary } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'ClaimTemplateModal';
const CREATE_FORM = 'ClaimTemplateCreateForm';

function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

function value(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
}

function modalRoot(): HTMLElement | null {
    return document.querySelector('.claimtemplate-modal');
}

export const List = {
    /**
     * Open the template detail in a modal. Detail.cshtml is a self-contained
     * modal-dialog, so no title is passed -- showModal drops the response into
     * #ClaimTemplateModal directly instead of wrapping a second header round it.
     */
    ShowDetail(templateId: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `ClaimTemplate/Detail/${templateId}`,
            title: null,
            errorTitle: `Template ${templateId}`,
            errorMessage: 'Template Detail failed to execute:',
        });
    },

    NewTemplate(): void {
        void ajaxModal({
            modal: MODAL,
            url: `ClaimTemplate/Create/${value('CurrentDomainId')}`,
            formId: CREATE_FORM,
            focusId: 'TemplateName',
            errorTitle: 'New Claim Template',
            errorMessage: 'Failed to render new template form',
        });
    },

    NewTemplateSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        setHidden(`#${CREATE_FORM} .fa-save`, true);
        setHidden(`#${CREATE_FORM} .fa-spinner`, false);
        void ajaxJson({
            url: 'ClaimTemplate',
            method: 'POST',
            data: serialize(form),
            alertInto: 'ClaimTemplateCreateMessageContainer',
            errorTitle: 'Failed to create new template',
            onSuccess: () => {
                hideModal(MODAL);
                toast('success', 'Template created');
                // The list is a PartialView, so a window.location.reload() would land
                // on a layout-less fragment. Re-fetch the domain route into the shell.
                void ajaxSwap({
                    container: 'ViewContainer',
                    url: `ClaimTemplate/Domain/${value('CurrentDomainId')}`,
                    alertTitle: 'Failed to reload templates',
                });
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState);
                toast('danger', 'Failed to create new template', summaryOf(data));
                return true;
            },
            onComplete: () => {
                setHidden(`#${CREATE_FORM} .fa-save`, false);
                setHidden(`#${CREATE_FORM} .fa-spinner`, true);
            },
        });
    },
};

/** One item operation's outcome. Never rejects -- a failure is a result, not a throw. */
interface OperationResult {
    ok: boolean;
}

function setRowError(row: Element, message: string | null): void {
    const host = row.querySelector('.ct-row-error');
    if (!host) return;
    host.textContent = message ?? '';
    host.classList.toggle('d-none', !message);
}

/**
 * Where a per-operation error is shown. Item rows carry a .ct-row-error span; the
 * Theme panel's controls do not, so setRowError is a no-op for them and their
 * failures surface only in SaveAll's count toast. That matches the legacy
 * behaviour -- giving every theme field its own error line is a markup change for
 * the panel, not for this module.
 */
function errorHostFor(node: Element): Element {
    return closest(node, 'tr') ?? closest(node, '.row') ?? node;
}

function saveHeader(data: Record<string, unknown>): Promise<OperationResult> {
    return new Promise(resolve => {
        void ajaxJson({
            url: 'ClaimTemplate/HeaderEdit',
            method: 'POST',
            data,
            onSuccess: () => resolve({ ok: true }),
            onFailure: response => {
                toast('danger', 'Header save failed', summaryOf(response));
                resolve({ ok: false });
                return true;
            },
            onError: (_xhr, status, error) => {
                toast('danger', 'Header save failed', error || status);
                resolve({ ok: false });
                return true;
            },
        });
    });
}

/**
 * `assignId` writes the server-assigned id back onto whatever identifies the row
 * for a retry. An item row keys off data-id + data-state; a Theme control keys off
 * data-tb-existing-id, and the legacy code wrote data-id onto it -- an attribute
 * nothing there reads. After a partial failure that left the new theme item
 * looking unsaved, so the retry POSTed it a second time and the template ended up
 * with two rows for the same key.
 */
function saveItem(
    model: Record<string, unknown>,
    node: Element,
    assignId: (id: number) => void,
): Promise<OperationResult> {
    const host = errorHostFor(node);
    return new Promise(resolve => {
        void ajaxJson({
            url: 'ClaimTemplate/ItemEdit',
            method: 'POST',
            data: model,
            onSuccess: response => {
                const id = (response as { id?: number } | null)?.id;
                if (id) assignId(id);
                setRowError(host, null);
                resolve({ ok: true });
            },
            onFailure: response => {
                const summary = summaryOf(response) || 'Save rejected by server.';
                setRowError(host, summary.replace(/<[^>]+>/g, ' ').trim());
                resolve({ ok: false });
                return true;
            },
            onError: (_xhr, status, error) => {
                setRowError(host, error || status || 'Save failed');
                resolve({ ok: false });
                return true;
            },
        });
    });
}

function deleteItem(itemId: number, node: Element): Promise<OperationResult> {
    const host = errorHostFor(node);
    return new Promise(resolve => {
        void ajaxJson({
            url: `ClaimTemplate/ItemDelete/${itemId}`,
            method: 'DELETE',
            onSuccess: () => {
                // Drop the row so the post-success re-fetch isn't strictly required
                // to "see" the deletion.
                host.remove();
                resolve({ ok: true });
            },
            onFailure: response => {
                const errors = (response as { errors?: string[] } | null)?.errors;
                setRowError(host, errors?.[0] ?? 'Delete rejected.');
                resolve({ ok: false });
                return true;
            },
            onError: (_xhr, status, error) => {
                setRowError(host, error || status || 'Delete failed');
                resolve({ ok: false });
                return true;
            },
        });
    });
}

function updateParentListRow(templateId: number, name: string, typeCode: string): void {
    const labels: Record<string, string> = { A: 'Application', R: 'Resource', P: 'Person', D: 'Domain' };
    const row = document.querySelector(`table.claimtemplate-list tr[data-id="${templateId}"]`);
    if (!row) return;
    const nameCell = row.querySelector('td.ctTemplateName');
    const typeCell = row.querySelector('td.ctTemplateType');
    if (nameCell) nameCell.textContent = name;
    if (typeCell) typeCell.textContent = labels[typeCode] ?? typeCode;
}

export const Detail = {
    EnterEditMode(): void {
        const modal = modalRoot();
        if (!modal || modal.classList.contains('template-edit-mode')) {
            // Idempotent re-entry: every theme-panel change triggers this, and
            // re-focusing on each keystroke would fight the operator for the caret.
            return;
        }
        modal.classList.add('template-edit-mode');
        // Only auto-focus the template name when edit mode was entered through the
        // pencil. A theme-panel interaction means the operator is already typing
        // somewhere -- leave focus where it is.
        const active = document.activeElement;
        const fromThemePanel = active instanceof Element
            && (closest(active, '.theme-branding-panel') !== null || active.id === 'ClaimTemplatePresetSelect');
        if (fromThemePanel) return;
        const name = modal.querySelector('.ct-template-name') as HTMLInputElement | null;
        name?.focus();
        name?.select();
    },

    CancelEdit(): void {
        void ajaxSwap({
            container: MODAL,
            url: `ClaimTemplate/Detail/${value('ClaimTemplateId')}`,
            alertTitle: 'Failed to reload template',
        });
    },

    AddItemRow(): void {
        const modal = modalRoot();
        const template = document.getElementById('ClaimTemplateNewRowTemplate');
        const body = modal?.querySelector('tbody.template-items');
        if (!modal || !body || !(template instanceof HTMLTemplateElement)) return;

        // Drop the "(no items / click + to add)" placeholder if it is still there.
        for (const empty of all('.claimtemplate-modal tr.ct-empty-row')) empty.remove();

        const fragment = template.content.cloneNode(true) as DocumentFragment;
        const row = fragment.querySelector('tr');
        body.appendChild(fragment);
        (row?.querySelector('.ct-item-name') as HTMLInputElement | null)?.focus();
    },

    /**
     * Apply the chosen palette preset to the Theme & Branding panel's colour
     * inputs. Bound to the preset dropdown's change -- picking a preset IS the
     * apply, there is no separate button. The preset map is JSON-serialized into
     * data-presets on .theme-branding-panel by the view. Each touched control is
     * marked data-tb-touched=1 so SaveAll knows to persist it.
     */
    ApplyPreset(): void {
        const panel = document.querySelector('.theme-branding-panel');
        const select = document.getElementById('ClaimTemplatePresetSelect') as HTMLSelectElement | null;
        const name = select?.value;
        if (!panel || !name) return;   // "(choose...)" -- nothing to do, no toast either

        let presets: Record<string, Record<string, string>> = {};
        try {
            presets = JSON.parse(panel.getAttribute('data-presets') ?? '{}') as Record<string, Record<string, string>>;
        } catch {
            presets = {};
        }
        const palette = presets[name];
        if (!palette) {
            toast('danger', 'Unknown preset', name);
            return;
        }

        let matched = 0;
        for (const input of panel.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.tb-input')) {
            const key = input.getAttribute('data-tb-key');
            if (!key || !Object.prototype.hasOwnProperty.call(palette, key)) continue;
            input.value = palette[key] ?? '';
            input.setAttribute('data-tb-touched', '1');
            // Sync the adjacent hex label so the view-mode swatches match.
            const hex = closest(input, '.row')?.querySelector('.tb-hex');
            if (hex) hex.textContent = palette[key] ?? '';
            matched++;
        }
        // Picking a preset implies "I am editing this template" -- flip the modal
        // into edit mode so the Save/Cancel/Delete footer appears. Idempotent.
        Detail.EnterEditMode();
        toast('success', `${name} preset applied`, `${matched} field(s) updated. Save to persist.`);
    },

    RemoveItemRow(this: Element): void {
        const row = closest<HTMLTableRowElement>(this, 'tr');
        if (!row) return;
        if (row.getAttribute('data-state') === 'new') {
            // Never persisted on the server -- discard the row outright.
            row.remove();
            return;
        }
        // Existing item: flag it, keep it visible so Cancel still reverts.
        row.setAttribute('data-state', 'removed');
        row.classList.add('ct-row-removed');
        for (const field of row.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select')) {
            field.disabled = true;
        }
    },

    Delete(templateId: number | string): void {
        // Resolve the displayed name so the prompt is specific. In edit mode it
        // lives in the .ct-template-name textbox, in view mode in the modal title.
        const modal = modalRoot();
        const name = (modal?.querySelector('.ct-template-name') as HTMLInputElement | null)?.value
            || modal?.querySelector('.modal-title')?.textContent?.trim()
            || '';
        void confirm({
            title: 'Delete Claim Template',
            message: name ? `Delete the '${name}' template?` : 'Delete this template?',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `ClaimTemplate/${templateId}`,
                    method: 'DELETE',
                    errorTitle: 'Delete failed',
                    onSuccess: () => {
                        hideModal(MODAL);
                        for (const row of all(`table.claimtemplate-list tr[data-id="${templateId}"]`)) row.remove();
                        toast('success', 'Template deleted');
                    },
                    onFailure: data => {
                        const summary = summaryOf(data)
                            || 'Server rejected the request. If your session is older than the most recent claim seeding, sign out and back in.';
                        toast('danger', 'Failed to delete template', summary);
                        return true;
                    },
                    onError: (_xhr, status, error) => {
                        toast('danger', 'Failed to delete template', error || status);
                        return true;
                    },
                });
            },
        });
    },

    SaveAll(): void {
        const modal = modalRoot();
        if (!modal) return;
        const templateId = parseInt(value('ClaimTemplateId'), 10) || 0;
        const domainId = parseInt(value('ClaimTemplateDomainId'), 10) || 0;

        // Client-side validation: every non-removed row needs a name and a claim
        // type. Surface the errors per row and abort if any fail.
        const rows = Array.from(modal.querySelectorAll('tbody.template-items tr[data-id]'));
        let invalid = false;
        for (const row of rows) {
            if (row.getAttribute('data-state') === 'removed') continue;
            const name = (row.querySelector('.ct-item-name') as HTMLInputElement | null)?.value.trim() ?? '';
            const claimType = (row.querySelector('.ct-item-claimtype') as HTMLSelectElement | null)?.value ?? '';
            setRowError(row, null);
            if (!name || !claimType) {
                setRowError(row, 'Item name and claim type are required.');
                invalid = true;
            }
        }
        if (invalid) {
            toast('warning', 'Fix the highlighted rows before saving');
            return;
        }

        const footerButtons = Array.from(modal.querySelectorAll<HTMLButtonElement>('.modal-footer button'));
        for (const button of footerButtons) button.disabled = true;
        setHidden('#ClaimTemplateBusyIndicator', false);

        const header = {
            Id: templateId,
            DomainId: domainId,
            TemplateName: (modal.querySelector('.ct-template-name') as HTMLInputElement | null)?.value ?? '',
            TemplateType: (modal.querySelector('.ct-template-type') as HTMLSelectElement | null)?.value ?? '',
        };

        const operations: Promise<OperationResult>[] = [saveHeader(header)];

        for (const row of rows) {
            const rowId = parseInt(row.getAttribute('data-id') ?? '', 10) || 0;
            const state = row.getAttribute('data-state');
            if (state === 'removed') {
                if (rowId > 0) operations.push(deleteItem(rowId, row));
                continue;
            }
            operations.push(saveItem({
                Id: rowId,
                TemplateId: templateId,
                Name: (row.querySelector('.ct-item-name') as HTMLInputElement | null)?.value ?? '',
                ClaimType: (row.querySelector('.ct-item-claimtype') as HTMLSelectElement | null)?.value ?? '',
                DefaultValue: (row.querySelector('.ct-item-defaultvalue') as HTMLInputElement | null)?.value || null,
                Required: (row.querySelector('.ct-item-required') as HTMLInputElement | null)?.checked ?? false,
            }, row, id => {
                row.setAttribute('data-id', String(id));
                row.setAttribute('data-state', 'unchanged');
            }));
        }

        // Theme & Branding panel: walk the dedicated inputs and emit the same
        // per-item operations the items list uses. An existing id always PUTs
        // (idempotent); an id of 0 only POSTs when touched=1, so we don't
        // manufacture empty rows the operator never engaged with. An existing
        // text/url field the operator cleared becomes a delete.
        // .tb-input is a CLASS, and one of them (the theme Mode control) is a
        // <select> -- typing the query as HTMLInputElement would have been a lie.
        for (const input of modal.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.theme-branding-panel .tb-input')) {
            const key = input.getAttribute('data-tb-key') ?? '';
            const existingId = parseInt(input.getAttribute('data-tb-existing-id') ?? '', 10) || 0;
            const touched = input.getAttribute('data-tb-touched') === '1';
            const name = input.getAttribute('data-tb-name') || key;
            const fieldValue = input.value.trim();

            if (existingId > 0) {
                if (fieldValue.length === 0) {
                    operations.push(deleteItem(existingId, input));
                } else {
                    operations.push(saveItem({
                        Id: existingId, TemplateId: templateId, Name: name,
                        ClaimType: key, DefaultValue: fieldValue, Required: false,
                    }, input, () => { /* already has its id */ }));
                }
            } else if (touched && fieldValue.length > 0) {
                operations.push(saveItem({
                    Id: 0, TemplateId: templateId, Name: name,
                    ClaimType: key, DefaultValue: fieldValue, Required: false,
                }, input, id => input.setAttribute('data-tb-existing-id', String(id))));
            }
        }

        void Promise.all(operations).then(results => {
            const failures = results.filter(result => !result.ok).length;
            setHidden('#ClaimTemplateBusyIndicator', true);
            for (const button of footerButtons) button.disabled = false;

            if (failures > 0) {
                toast('danger', 'Some changes failed',
                    `${failures} of ${results.length} operations failed — see highlighted rows`);
                return;
            }
            updateParentListRow(templateId, header.TemplateName, header.TemplateType);
            toast('success', 'Template saved');
            // Re-fetch the detail: resets to view mode and brings in anything the
            // server assigned, DisplayOrder included.
            void ajaxSwap({
                container: MODAL,
                url: `ClaimTemplate/Detail/${templateId}`,
                alertTitle: 'Template saved but failed to refresh',
            });
        });
    },
};

/**
 * Theme-panel wiring, run over every swap that carries the panel.
 *
 * Any change on a Theme control marks the field touched so SaveAll persists it,
 * syncs the .tb-hex label for colour pickers, and flips the modal into edit mode
 * so the Save/Cancel footer appears -- "interaction implies intent to edit", so
 * operators don't have to find the pencil before changing a colour.
 *
 * The legacy pair of document-level jQuery delegations ran at file scope, which
 * meant they bound once at load and only ever for the document. An enhancer binds
 * to the panel that actually landed, and `input` on a colour control is what makes
 * the hex label track a drag rather than only the final release.
 */
export function enhanceThemePanel(root: ParentNode): void {
    const panel = root instanceof Element && root.matches('.theme-branding-panel')
        ? root
        : root.querySelector('.theme-branding-panel');
    if (!panel) return;

    const touch = (event: Event): void => {
        const input = event.target;
        // Not `instanceof HTMLInputElement`: the theme Mode control is a <select>,
        // and narrowing to inputs would have left it unmarked, so changing the
        // mode alone never persisted.
        if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
        input.setAttribute('data-tb-touched', '1');
        if (input instanceof HTMLInputElement && input.type === 'color') {
            const hex = closest(input, '.row')?.querySelector('.tb-hex');
            if (hex) hex.textContent = input.value;
        }
        Detail.EnterEditMode();
    };
    for (const input of panel.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.tb-input')) {
        input.addEventListener('change', touch);
        input.addEventListener('input', touch);
    }

    // Selecting a palette IS the apply. A <select data-action> would reach
    // ApplyPreset through core/dispatch's change path, but the preset control is
    // rendered by _ThemePanel.cshtml without one, so bind it here.
    const preset = panel.querySelector('#ClaimTemplatePresetSelect')
        ?? document.getElementById('ClaimTemplatePresetSelect');
    preset?.addEventListener('change', () => Detail.ApplyPreset());
}
